import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Menu,
  Dropdown,
  Form,
  Input,
  Button,
  Grid,
  message,
  Upload,
  Checkbox,
  Select,
} from "antd";
import { Link } from "react-router-dom";
import { MenuOutlined, UploadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import propertyImage from "../assets/property.png";
import CommonFooter from "../components/common/CommonFooter";
import { firebaseService } from "../lib/services/firebaseService";
import { Property } from "../types/propert";
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ListPropertyFormPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // Get user data from navigation state
  const userData = location.state?.userData || {
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    listedBy: "",
  };

  const { Option } = Select;

  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    propertyType: "",
    propertyStatus: "",
    moreDetails: "",
    multipleUnits: "",
    bua: "",
    plotSize: "",
    rooms: "",
    bathrooms: "",
    otherFeatures: "",
    ownershipStatus: "",
    mortgageStatus: "",
    valuationReport: "",
    listPrice: "",
    finalPrice: "",
    listedElsewhere: "",
    listedWhere: "",
    marketingPreference: "",
    discloseDetails: "",
    listingInstructions: "",
    notifyProgress: false,
    agreeTerms: false,
  });

  const [address, setAddress] = useState({
    country: "",
    city: "",
    area: "",
    street: "",
    googleMap: "",
    landmark: "",
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Define static country and city options
  const COUNTRIES = [
    { value: "UAE", label: "United Arab Emirates" },
    { value: "SA", label: "Saudi Arabia" },
    { value: "QA", label: "Qatar" },
    { value: "OM", label: "Oman" },
  ];

  const CITIES = {
    UAE: [
      { value: "Dubai", label: "Dubai" },
      { value: "Abu Dhabi", label: "Abu Dhabi" },
      { value: "Sharjah", label: "Sharjah" },
    ],
    SA: [
      { value: "Riyadh", label: "Riyadh" },
      { value: "Jeddah", label: "Jeddah" },
    ],
    QA: [{ value: "Doha", label: "Doha" }],
    OM: [{ value: "Muscat", label: "Muscat" }],
    // Add other cities for other countries as needed
  };

  const handleOptionClick = (field, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (field, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field, value) => {
    setAddress((prev) => {
      if (field === "country" && value !== prev.country) {
        return {
          ...prev,
          [field]: value,
          city: "", 
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleCheckboxChange = (field, checked) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleFileUpload = async (file) => {
    try {
      const imageUrl = await firebaseService.uploadImageFile(
        file,
        `${Date.now()}-${file.name}`
      );
      setUploadedFiles((prev) => [...prev, imageUrl]);
      return false; // Prevent default upload behavior
    } catch (error) {
      message.error("Failed to upload file");
      return false;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Validation
      if (!selectedOptions.agreeTerms) {
        message.error("Please agree to the terms and conditions");
        return;
      }

      if (!address.country || !address.city || !address.area) {
        message.error("Please fill in required address fields");
        return;
      }

      if (!selectedOptions.listPrice) {
        message.error("Please enter list price");
        return;
      }

      // Create property object using user data from previous page
      const property = new Property({
        id: `property-${Date.now()}`,
        // User data from previous page
        userName: userData.name,
        userEmail: userData.email,
        userContact: userData.mobile,
        userWhatsApp: userData.whatsapp,
        listedBy: userData.listedBy,

        // Address Details
        country: address.country,
        city: address.city,
        area: address.area,
        street: address.street,
        googleMap: address.googleMap,
        landmark: address.landmark,

        // Property Specifications
        propertyType: selectedOptions.propertyType,
        furnishing: selectedOptions.moreDetails,
        propertyStatus: selectedOptions.propertyStatus,

        // Size Details
        builtUpArea: parseFloat(selectedOptions.bua) || 0,
        plotSize: parseFloat(selectedOptions.plotSize) || 0,
        areaUnit: "sq.ft",

        // Room Details
        bedrooms: parseInt(selectedOptions.rooms) || 0,
        bathrooms: parseInt(selectedOptions.bathrooms) || 0,
        livingRooms: 0, // You can add this field to your form if needed
        kitchens: 0, // You can add this field to your form if needed
        parkingSpaces: 0, // You can add this field to your form if needed

        // Listing Information
        listedBy: "Owner", // You can get this from form or set default
        ownershipStatus: selectedOptions.ownershipStatus,
        mortgageStatus: selectedOptions.mortgageStatus,
        exclusivity: selectedOptions.listedElsewhere === "No",

        // Pricing Information
        listPrice: parseFloat(selectedOptions.listPrice) || 0,
        finalPrice:
          parseFloat(selectedOptions.finalPrice) ||
          parseFloat(selectedOptions.listPrice) ||
          0,
        currency: "AED",
        priceNegotiable: true, // You can add this field to your form if needed

        // Marketing Preferences
        marketingPreference:
          selectedOptions.marketingPreference === "List and Promote"
            ? "listAndPromote"
            : "listOnly",
        webListingDetails: {
          showPrice: true,
          showBasicDetails: selectedOptions.discloseDetails === "Basic Details",
          showPictures: selectedOptions.discloseDetails === "Pics",
          showLocation: selectedOptions.discloseDetails === "Geo Location",
        },

        // Additional Instructions
        listingInstructions: selectedOptions.listingInstructions,
        specialFeatures: [],
        otherFeatures: selectedOptions.otherFeatures,

        // Media
        imageUrls: uploadedFiles,
        videoUrl: "",
        floorPlans: [],

        // Description
        description: selectedOptions.otherFeatures,
        title: `${selectedOptions.propertyType} in ${address.area}, ${address.city}`,

        // Status
        status: "pending",
      });

      // Save to Firebase
      await firebaseService.addOrUpdateProperty(property);

      message.success(
        "Property listed successfully! Waiting for admin approval."
      );

      // Reset form
      setSelectedOptions({
        propertyType: "",
        propertyStatus: "",
        moreDetails: "",
        multipleUnits: "",
        bua: "",
        plotSize: "",
        rooms: "",
        bathrooms: "",
        otherFeatures: "",
        ownershipStatus: "",
        mortgageStatus: "",
        valuationReport: "",
        listPrice: "",
        finalPrice: "",
        listedElsewhere: "",
        listedWhere: "",
        marketingPreference: "",
        discloseDetails: "",
        listingInstructions: "",
        notifyProgress: false,
        agreeTerms: false,
      });

      setAddress({
        country: "",
        city: "",
        area: "",
        street: "",
        googleMap: "",
        landmark: "",
      });

      setUploadedFiles([]);
    } catch (error) {
      console.error("Error submitting property:", error);
      message.error("Failed to list property. Please try again.");
    } finally {
      setLoading(false);
      navigate("/confirmation");
    }
  };

  const menu = (
    <Menu
      theme="dark"
      style={{
        background: "rgba(0, 0, 0, 0.95)",
        color: "#fff",
        padding: "10px 25px",
        borderRadius: "6px",
      }}
      items={[
        {
          key: "0",
          label: (
            <Link to="/" style={{ color: "white" }}>
              Home
            </Link>
          ),
        },
        {
          key: "1",
          label: (
            <Link to="/about-us" style={{ color: "white" }}>
              Who we are
            </Link>
          ),
        },
        {
          key: "2",
          label: (
            <Link to="/listed-property" style={{ color: "white" }}>
              Our offerings
            </Link>
          ),
        },

        {
          key: "3",
          label: (
            <Link to="/work" style={{ color: "white" }}>
              How NITM works
            </Link>
          ),
        },
        {
          key: "4",
          label: (
            <Link to="/policy" style={{ color: "white" }}>
              List your Property
            </Link>
          ),
        },
        {
          key: "5",
          label: (
            <Link to="/news-room" style={{ color: "white" }}>
              News Room
            </Link>
          ),
        },
        {
          key: "6",
          label: (
            <Link to="/contact-us" style={{ color: "white" }}>
              Contact Us
            </Link>
          ),
        },
        {
          key: "7",
          label: (
            <Link to="/our-story" style={{ color: "white" }}>
              Our Story
            </Link>
          ),
        },
      ]}
    />
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f0f0f0" }}>
      <Row style={{ minHeight: "100vh" }}>
        {/* Left Image Section */}
        {screens.md && (
          <Col md={12} style={{ position: "relative" }}>
            <img
              src={propertyImage}
              alt="Property"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: screens.xs ? "12px" : "0 0 140px 0",
              }}
            />
            <Link to="/" style={{ display: "inline-block" }}>
              <img
                src={logo}
                alt="Logo"
                style={{ position: "absolute", top: 20, left: 20, height: 40 }}
              />
            </Link>
          </Col>
        )}

        {/* Right Section */}
        <Col
          xs={24}
          md={12}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "#f0f0f0",
            minHeight: "100vh",
            position: "relative",
          }}
        >
          {/* Header */}
          <div
            style={{
              width: "100%",
              background: "#DDC7BB",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: 80,
            }}
          >
            <Title
              level={4}
              style={{ margin: "0 20px", color: "#000" }}
            ></Title>

            <Dropdown overlay={menu} placement="bottomRight">
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#D9D9D9E5",
                  padding: "0 20px",
                  cursor: "pointer",
                }}
              >
                {/* Custom Hamburger Icon */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    alignItems: "flex-end",
                  }}
                >
                  <span
                    style={{
                      width: "24px",
                      height: "3px",
                      background: "black",
                      borderRadius: "2px",
                    }}
                  ></span>
                  <span
                    style={{
                      width: "18px",
                      height: "3px",
                      background: "black",
                      borderRadius: "2px",
                    }}
                  ></span>
                  <span
                    style={{
                      width: "12px",
                      height: "3px",
                      background: "black",
                      borderRadius: "2px",
                    }}
                  ></span>
                </div>
              </div>
            </Dropdown>
          </div>

          {/* Property Details Form */}
          <div
            style={{
              maxWidth: 500,
              width: "100%",
              padding: "40px 40px",
              marginTop: 30,
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              overflowY: "auto",
              maxHeight: "80vh",
            }}
          >
            <Title level={4} style={{ textAlign: "center", marginBottom: 20 }}>
              Property Details
            </Title>

            <Form layout="vertical">
              {/* Property Type */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    What type of property are you listing?
                  </span>
                }
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {["Villa", "Building", "Plot", "Apartment"].map((type) => (
                    <Button
                      key={type}
                      shape="round"
                      onClick={() => handleOptionClick("propertyType", type)}
                      style={{
                        border:
                          selectedOptions.propertyType === type
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.propertyType === type
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.propertyType === type
                            ? "#fff"
                            : "#000",
                        width: 130,
                        borderRadius: 6,
                      }}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Property Status */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Is the property?
                  </span>
                }
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {["Off Plan", "Ready", "Distress", "Other"].map((status) => (
                    <Button
                      key={status}
                      shape="round"
                      onClick={() =>
                        handleOptionClick("propertyStatus", status)
                      }
                      style={{
                        border:
                          selectedOptions.propertyStatus === status
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.propertyStatus === status
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.propertyStatus === status
                            ? "#fff"
                            : "#000",
                        width: 130,
                        borderRadius: 6,
                      }}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* More Details */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    More details:
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["Furnished", "Unfurnished"].map((detail) => (
                    <Button
                      key={detail}
                      shape="round"
                      onClick={() => handleOptionClick("moreDetails", detail)}
                      style={{
                        border:
                          selectedOptions.moreDetails === detail
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.moreDetails === detail
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.moreDetails === detail
                            ? "#fff"
                            : "#000",
                        width: 150,
                        borderRadius: 6,
                      }}
                    >
                      {detail}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Multiple Units */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Would you like to list multiple units under the same
                    project?
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["Yes", "No"].map((opt) => (
                    <Button
                      key={opt}
                      shape="round"
                      onClick={() => handleOptionClick("multipleUnits", opt)}
                      style={{
                        border:
                          selectedOptions.multipleUnits === opt
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.multipleUnits === opt
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.multipleUnits === opt
                            ? "#fff"
                            : "#000",
                        width: 100,
                        borderRadius: 6,
                      }}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Property Dimensions */}
              <Title
                level={5}
                style={{
                  color: "#000",
                  fontWeight: 600,
                  marginTop: 30,
                  marginBottom: 15,
                }}
              >
                Property Dimensions (Sq. Ft.)
              </Title>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    BUA
                  </Text>
                  <Input
                    value={selectedOptions.bua}
                    onChange={(e) => handleInputChange("bua", e.target.value)}
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Plot Size
                  </Text>
                  <Input
                    value={selectedOptions.plotSize}
                    onChange={(e) =>
                      handleInputChange("plotSize", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Rooms
                  </Text>
                  <Input
                    value={selectedOptions.rooms}
                    onChange={(e) => handleInputChange("rooms", e.target.value)}
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Bathrooms
                  </Text>
                  <Input
                    value={selectedOptions.bathrooms}
                    onChange={(e) =>
                      handleInputChange("bathrooms", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
              </Row>

              {/* Other Features */}
              <Title
                level={5}
                style={{
                  color: "#000",
                  fontWeight: 600,
                  marginTop: 30,
                  marginBottom: 15,
                }}
              >
                Other Features
              </Title>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Describe Other Features
              </Text>
              <Input.TextArea
                rows={4}
                value={selectedOptions.otherFeatures}
                onChange={(e) =>
                  handleInputChange("otherFeatures", e.target.value)
                }
                style={{ background: "#f9f9f9" }}
              />

              {/* Address Section */}
              <Title
                level={5}
                style={{
                  color: "#000",
                  fontWeight: 600,
                  marginTop: 30,
                  marginBottom: 15,
                }}
              >
                Address of the Property <span style={{ color: "red" }}>*</span>
              </Title>

              <Row gutter={[16, 16]}>
                {/* Country Dropdown (REPLACEMENT) */}
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Country
                  </Text>
                  <Select
                    value={address.country || undefined}
                    onChange={(value) => handleAddressChange("country", value)}
                    style={{ width: "100%", height: 40 }}
                    placeholder="Select Country"
                  >
                    {COUNTRIES.map((c) => (
                      <Option key={c.value} value={c.value}>
                        {c.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                {/* City Dropdown (REPLACEMENT) */}
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    City
                  </Text>
                  <Select
                    value={address.city || undefined}
                    onChange={(value) => handleAddressChange("city", value)}
                    style={{ width: "100%", height: 40 }}
                    placeholder="Select City"
                    disabled={!address.country}
                  >
                    {address.country && CITIES[address.country] ? (
                      CITIES[address.country].map((c) => (
                        <Option key={c.value} value={c.value}>
                          {c.label}
                        </Option>
                      ))
                    ) : (
                      <Option key="none" value="" disabled>
                        Select a Country first
                      </Option>
                    )}
                  </Select>
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Area
                  </Text>
                  <Input
                    value={address.area}
                    onChange={(e) =>
                      handleAddressChange("area", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Street
                  </Text>
                  <Input
                    value={address.street}
                    onChange={(e) =>
                      handleAddressChange("street", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Google Map / Makani No
                  </Text>
                  <Input
                    value={address.googleMap}
                    onChange={(e) =>
                      handleAddressChange("googleMap", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Landmark
                  </Text>
                  <Input
                    value={address.landmark}
                    onChange={(e) =>
                      handleAddressChange("landmark", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
              </Row>

              {/* Ownership Status */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Ownership Status:
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["Single Owner", "Joint"].map((owner) => (
                    <Button
                      key={owner}
                      shape="round"
                      onClick={() =>
                        handleOptionClick("ownershipStatus", owner)
                      }
                      style={{
                        border:
                          selectedOptions.ownershipStatus === owner
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.ownershipStatus === owner
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.ownershipStatus === owner
                            ? "#fff"
                            : "#000",
                        width: 140,
                        borderRadius: 6,
                      }}
                    >
                      {owner}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Mortgage Status */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Mortgage Status:
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["Mortgaged", "Fully Paid"].map((status) => (
                    <Button
                      key={status}
                      shape="round"
                      onClick={() =>
                        handleOptionClick("mortgageStatus", status)
                      }
                      style={{
                        border:
                          selectedOptions.mortgageStatus === status
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.mortgageStatus === status
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.mortgageStatus === status
                            ? "#fff"
                            : "#000",
                        width: 140,
                        borderRadius: 6,
                      }}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Valuation Report */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Do you have a recent valuation report or RERA-approved
                    appraisal?
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["Yes", "No"].map((opt) => (
                    <Button
                      key={opt}
                      shape="round"
                      onClick={() => handleOptionClick("valuationReport", opt)}
                      style={{
                        border:
                          selectedOptions.valuationReport === opt
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.valuationReport === opt
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.valuationReport === opt
                            ? "#fff"
                            : "#000",
                        width: 100,
                        borderRadius: 6,
                      }}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Sales Price Section */}
              <Title
                level={5}
                style={{
                  color: "#000",
                  fontWeight: 600,
                  marginTop: 30,
                  marginBottom: 15,
                }}
              >
                Sales Price
              </Title>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    List Price (AED)
                  </Text>
                  <Input
                    placeholder="Enter list price"
                    value={selectedOptions.listPrice}
                    onChange={(e) =>
                      handleInputChange("listPrice", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>
                    Final Price (AED)
                  </Text>
                  <Input
                    placeholder="Enter final price"
                    value={selectedOptions.finalPrice}
                    onChange={(e) =>
                      handleInputChange("finalPrice", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Col>
              </Row>

              {/* Is property listed elsewhere */}
              <Title
                level={5}
                style={{
                  color: "#000",
                  fontWeight: 600,
                  marginTop: 30,
                  marginBottom: 15,
                }}
              >
                Is the property currently listed elsewhere (e.g., portals, other
                brokers)?
              </Title>
              <div style={{ display: "flex", gap: "10px" }}>
                {["Yes", "No"].map((opt) => (
                  <Button
                    key={opt}
                    shape="round"
                    onClick={() => handleOptionClick("listedElsewhere", opt)}
                    style={{
                      border:
                        selectedOptions.listedElsewhere === opt
                          ? "2px solid #DDC7BB"
                          : "1px solid #ccc",
                      background:
                        selectedOptions.listedElsewhere === opt
                          ? "#DDC7BB"
                          : "#fff",
                      color:
                        selectedOptions.listedElsewhere === opt
                          ? "#fff"
                          : "#000",
                      width: 100,
                      borderRadius: 6,
                    }}
                  >
                    {opt}
                  </Button>
                ))}
              </div>

              {/* If yes, specify where */}
              {selectedOptions.listedElsewhere === "Yes" && (
                <Form.Item
                  label={
                    <span style={{ fontWeight: "600", color: "#000" }}>
                      If yes, please specify where
                    </span>
                  }
                >
                  <Input
                    placeholder="Enter details (e.g., Property Finder, Bayut, etc.)"
                    value={selectedOptions.listedWhere}
                    onChange={(e) =>
                      handleInputChange("listedWhere", e.target.value)
                    }
                    style={{ background: "#f9f9f9", height: 40 }}
                  />
                </Form.Item>
              )}

              {/* Marketing Preferences */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    What are your marketing preferences?
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  {["List Only", "List and Promote"].map((opt) => (
                    <Button
                      key={opt}
                      shape="round"
                      onClick={() =>
                        handleOptionClick("marketingPreference", opt)
                      }
                      style={{
                        border:
                          selectedOptions.marketingPreference === opt
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.marketingPreference === opt
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.marketingPreference === opt
                            ? "#fff"
                            : "#000",
                        width: 160,
                        borderRadius: 6,
                      }}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Disclose Details */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Disclose only the following details on web listing:
                  </span>
                }
              >
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {["Basic Details", "Pics", "Geo Location"].map((opt) => (
                    <Button
                      key={opt}
                      shape="round"
                      onClick={() => handleOptionClick("discloseDetails", opt)}
                      style={{
                        border:
                          selectedOptions.discloseDetails === opt
                            ? "2px solid #DDC7BB"
                            : "1px solid #ccc",
                        background:
                          selectedOptions.discloseDetails === opt
                            ? "#DDC7BB"
                            : "#fff",
                        color:
                          selectedOptions.discloseDetails === opt
                            ? "#fff"
                            : "#000",
                        width: 140,
                        borderRadius: 6,
                      }}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              </Form.Item>

              {/* Listing Instructions */}
              <Form.Item
                label={
                  <span style={{ fontWeight: "600", color: "#000" }}>
                    Any instructions on the listing and promotion of your
                    property
                  </span>
                }
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter special instructions or comments"
                  value={selectedOptions.listingInstructions}
                  onChange={(e) =>
                    handleInputChange("listingInstructions", e.target.value)
                  }
                  style={{ background: "#f9f9f9" }}
                />
              </Form.Item>

              {/* Attach Files */}
              <div style={{ marginTop: 20 }}>
                <Upload
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  multiple
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{
                      border: "1px solid #ccc",
                      color: "#000",
                      width: "100%",
                      height: 45,
                      fontWeight: 500,
                    }}
                  >
                    Attach Files ({uploadedFiles.length} uploaded)
                  </Button>
                </Upload>

                <div style={{ marginTop: 20 }}>
                  <Form.Item>
                    <Checkbox
                      checked={selectedOptions.notifyProgress}
                      onChange={(e) =>
                        handleCheckboxChange("notifyProgress", e.target.checked)
                      }
                      style={{ marginRight: 8 }}
                    >
                      Notify me on all progress stages
                    </Checkbox>

                    <br />

                    <Checkbox
                      checked={selectedOptions.agreeTerms}
                      onChange={(e) =>
                        handleCheckboxChange("agreeTerms", e.target.checked)
                      }
                      style={{ marginRight: 8 }}
                    >
                      I have read and agree to the terms & conditions
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>

              {/* Save Button */}
              <Button
                type="primary"
                block
                loading={loading}
                onClick={handleSubmit}
                style={{
                  background: "#000",
                  color: "#fff",
                  height: 50,
                  fontSize: 16,
                  fontWeight: 600,
                  marginTop: 30,
                }}
              >
                Save the Listing
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
      <CommonFooter />
    </div>
  );
};

export default ListPropertyFormPage;
